import { useEffect, useState } from "react";
import { bloomsApi } from "../services/bloomsApi";
import { useToast } from "../context/ToastContext";
import { Card } from "../components/common/Card";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { DataTable } from "../components/common/DataTable";
import { Tabs } from "../components/common/Tabs";
import { Modal } from "../components/common/Modal";

const PRIORITY_KEY = "beekeeper.priority_plants";

function getPriorities() {
  try {
    return new Set(JSON.parse(localStorage.getItem(PRIORITY_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

export function BloomCalendarPage() {
  const { notify } = useToast();
  const [plants, setPlants] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [priorities, setPriorities] = useState(getPriorities);
  const [search, setSearch] = useState("");
  const [observeOpen, setObserveOpen] = useState(false);
  const [observeForm, setObserveForm] = useState({ plant_id: "", year: new Date().getFullYear(), actual_bloom_start: "", actual_bloom_end: "" });

  useEffect(() => {
    bloomsApi.plants().then((r) => setPlants(r.plants));
    bloomsApi.calendar().then((r) => setCalendar(r.bloom_calendar));
  }, []);

  function togglePriority(plantId) {
    const next = new Set(priorities);
    if (next.has(plantId)) next.delete(plantId);
    else next.add(plantId);
    localStorage.setItem(PRIORITY_KEY, JSON.stringify([...next]));
    setPriorities(next);
  }

  async function submitObservation() {
    if (!observeForm.plant_id) return notify("Choose a plant", "warning");
    try {
      await bloomsApi.observe({
        plant_id: Number(observeForm.plant_id),
        year: Number(observeForm.year),
        actual_bloom_start: observeForm.actual_bloom_start || undefined,
        actual_bloom_end: observeForm.actual_bloom_end || undefined,
      });
      notify("Bloom observation logged", "success");
      setObserveOpen(false);
    } catch (err) {
      notify(err.apiMessage || "Could not log observation", "danger");
    }
  }

  const filteredPlants = plants?.filter((p) => p.plant_name.toLowerCase().includes(search.toLowerCase()));

  const tabs = [
    {
      key: "calendar",
      label: "Bloom Calendar",
      content: (
        <DataTable
          rows={calendar}
          loading={!calendar}
          rowKey="match_id"
          emptyTitle="No bloom matches yet"
          emptyDescription="Generate a bloom calendar for your sites from Hive Detail's Bloom tab, or via the backend sp_GenerateBloomCalendar procedure."
          columns={[
            { key: "dock_name", header: "Site" },
            { key: "plant_name", header: "Plant" },
            { key: "bloom_start_date", header: "Start", render: (r) => new Date(r.bloom_start_date).toLocaleDateString() },
            { key: "bloom_end_date", header: "End", render: (r) => new Date(r.bloom_end_date).toLocaleDateString() },
          ]}
        />
      ),
    },
    {
      key: "plants",
      label: "Forage Plants",
      content: (
        <div>
          <input id="bloom-calendar-page-search" name="search" placeholder="Search plants…" value={search} onChange={(e) => setSearch(e.target.value)} className="touch-target section-spacing" style={{ width: "100%", maxWidth: 320 }} />
          <DataTable
            rows={filteredPlants}
            loading={!plants}
            rowKey="plant_id"
            emptyTitle="No matching plants"
            emptyDescription="Nothing matches that search. Try a shorter word, or clear the box to see them all."
            columns={[
              { key: "plant_name", header: "Plant" },
              { key: "scientific_name", header: "Scientific Name" },
              { key: "bloom_start_month", header: "Bloom Months", render: (r) => `${r.bloom_start_month}–${r.bloom_end_month}` },
              { key: "nectar_value", header: "Nectar", render: (r) => `${r.nectar_value}/10` },
              {
                key: "priority",
                header: "Priority",
                render: (r) => (
                  <Button size="sm" variant={priorities.has(r.plant_id) ? "primary" : "secondary"} onClick={() => togglePriority(r.plant_id)}>
                    {priorities.has(r.plant_id) ? <><Badge tone="honey">★</Badge> Priority</> : "Mark Priority"}
                  </Button>
                ),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Bloom Calendar">
        <Button onClick={() => setObserveOpen(true)}>Log Bloom Observation</Button>
      </PageHeader>

      <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Note: plant prioritization (★) is stored locally in this browser only -- api/functions/blooms.py has no endpoint for
        app.UserForagePreferences yet, so priorities don't sync across devices until that's added.
      </p>

      <Card><Tabs tabs={tabs} /></Card>

      <Modal open={observeOpen} onClose={() => setObserveOpen(false)} title="Log Bloom Observation" footer={<Button onClick={submitObservation}>Save</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <select id="bloom-calendar-page-plant-id" name="plant-id" value={observeForm.plant_id} onChange={(e) => setObserveForm((f) => ({ ...f, plant_id: e.target.value }))} className="touch-target">
            <option value="">Choose plant…</option>
            {plants?.map((p) => <option key={p.plant_id} value={p.plant_id}>{p.plant_name}</option>)}
          </select>
          <input id="bloom-calendar-page-year" name="year" type="number" value={observeForm.year} onChange={(e) => setObserveForm((f) => ({ ...f, year: e.target.value }))} className="touch-target" />
          <label>Actual bloom start<input id="bloom-calendar-page-actual-bloom-start" name="actual-bloom-start" type="date" value={observeForm.actual_bloom_start} onChange={(e) => setObserveForm((f) => ({ ...f, actual_bloom_start: e.target.value }))} className="touch-target" style={{ width: "100%" }} /></label>
          <label>Actual bloom end<input id="bloom-calendar-page-actual-bloom-end" name="actual-bloom-end" type="date" value={observeForm.actual_bloom_end} onChange={(e) => setObserveForm((f) => ({ ...f, actual_bloom_end: e.target.value }))} className="touch-target" style={{ width: "100%" }} /></label>
        </div>
      </Modal>
    </div>
  );
}
